import { computed, Inject, Injectable, LOCALE_ID, signal } from '@angular/core';
import { AlertController, ToastController } from '@ionic/angular';
import { Preferences } from '@capacitor/preferences';
import { TranslateService } from '@ngx-translate/core';
import { CldrIntlService, IntlService } from '@progress/kendo-angular-intl';
import { Md5 } from 'ts-md5';

import { BackendService } from './backend/backend.service';
import { ELingua, ENaarGoResponseResult, INaarGoChangePasswordRequest, INaarGoCheckRequest, INaarGoConfirmEmailRequest, INaarGoDeleteUserRequest, INaarGoEditUserRequest, INaarGoEditUserResponse, INaarGoLoginRequest, INaarGoLoginResponse, INaarGoRequest, INaarGoResendCodeRequest, INaarGoSignupRequest, INaarGoTokenResponse, IOkNaarGoResponse, NaarGoCheckResponse, NaarGoUserRow } from './backend/naar-api-client';
import { UrlService } from './url.service';
import { AvailableLangCodes, ELinguaToLangCode } from '../types/languages.type';
import { StorageService } from './storage.service';
import { TripService } from './trip.service';

const USER_TOKEN_KEY = 'naargotnk';
const USER_IDENTITY_KEY = 'naargocsi';

@Injectable({
	providedIn: 'root'
})
export class UserService {

	public currentUser$ = signal<NaarGoUserRow | null>(null);
	public isLoggedIn$ = computed(() => this.currentUser$() != null);

	public enableDebugInfo = false;

	/** The encrypted trip ID parameter to use after auth */
	public tripIdParam: string = null;

	private readonly TOAST_DURATION = 2000;

	constructor(
		private backend: BackendService,
		private url: UrlService,
		private translate: TranslateService,
		private storage: StorageService,
		private toast: ToastController,
		private alert: AlertController,
		private tripService: TripService,
		@Inject(LOCALE_ID) public localeId: string,
		public intlService: IntlService,
	) {
	}

	public async init(): Promise<void> {
		await this.doCheck();
		await this.setAppLanguage();
	}

	/**
	 * Sets the application language
	 * @param language The language to set. If not provided, the current user language or the system language will be used
	 */
	public async setAppLanguage(language?: ELingua) {

		if (!language) {
			language = this.currentUser$()?.language ?? await this.storage.getSystemLanguage();
		}

		const langCode = ELinguaToLangCode(language);
		if (langCode == this.translate.currentLang)
			return;

		console.log(`Setting language to '${langCode}'`);
		if (!AvailableLangCodes.includes(langCode)) {
			console.warn(`Language code '${langCode}' is not supported.`);
			return;
		}

		// Update the language in the translate service
		this.translate.use(langCode);

		// Changing language for Kendo pipes & co
		this.localeId = langCode == 'zu' ? 'it' : langCode;
		(<CldrIntlService>this.intlService).localeId = this.localeId;
	}

	/**
	 * Verifica sul server se l'utente è loggato o meno
	 */
	public async doCheck(): Promise<boolean> {
		const csIdentity = (await Preferences.get({ key: USER_IDENTITY_KEY })).value || null;
		if (!csIdentity)
			return false;

		const checkRequest: INaarGoCheckRequest = {
			csIdentity: csIdentity
		};
		try {
			const checkResponse = await this.backend.post<NaarGoCheckResponse>('auth/check', checkRequest);
			if (checkResponse.result == ENaarGoResponseResult.Ok) {
				this.storeUser(checkResponse.user);
				await this.refreshUserToken(csIdentity);
				await this.tripService.init();
			}
			if (checkResponse.isDebug) {
				this.enableDebugInfo = true;
				console.log('Server:', checkResponse.naarDatabaseServer, 'Database:', checkResponse.naarDatabaseName);
			}
		} catch (error) {
			console.error(error);
			await Preferences.remove({ key: USER_TOKEN_KEY });
			await Preferences.remove({ key: USER_IDENTITY_KEY });
		}
		return this.isLoggedIn$();
	}

	/**
	 * Esegue il login di un utente
	 * @param emailAddress Email
	 * @param password Password
	 */
	public async login(emailAddress: string, password: string): Promise<boolean> {

		const md5 = new Md5();
		md5.appendStr(password);
		const encrPsw = md5.end()?.toString() || '';

		const loginData: INaarGoLoginRequest = {
			emailAddress: emailAddress,
			password: encrPsw,
		};

		const loginResponse = await this.backend.post<INaarGoLoginResponse>('auth/login', loginData);
		this.storeUser(loginResponse.user);
		await this.tripService.init();
		return this.isLoggedIn$();
	}

	/**
	 * Esegue il signup di un utente
	 * @returns true se l'utente è stato registrato correttamente
	 */
	public async signup(name: string, lastname: string, emailAddress: string, password: string, language: ELingua): Promise<boolean> {

		const md5 = new Md5();
		md5.appendStr(password);
		const encrPsw = md5.end()?.toString() || '';

		const signupData: INaarGoSignupRequest = {
			name: name,
			lastname: lastname,
			emailAddress: emailAddress,
			password: encrPsw,
			language: language,
		};

		const loginResponse = await this.backend.post<IOkNaarGoResponse>('auth/signup', signupData);
		return loginResponse.result == ENaarGoResponseResult.Ok;
	}

	public async resendConfirmEmail(email: string) {
		const request: INaarGoResendCodeRequest = {
			emailAddress: email,
		};
		const response = await this.backend.post<IOkNaarGoResponse>(`auth/resend_code`, request);
		return response.result == ENaarGoResponseResult.Ok;
	}

	public async confirmEmail(email: string, code: string) {
		const request: INaarGoConfirmEmailRequest = {
			code: code,
			emailAddress: email,
		};
		const response = await this.backend.post<IOkNaarGoResponse>(`auth/confirm_email`, request);
		return response.result == ENaarGoResponseResult.Ok;
	}

	/**
	 * Richiede l'invio di un codice OTP per il recupero password.
	 * Usato sia per la richiesta iniziale sia per il reinvio del codice.
	 * @returns true se la richiesta è andata a buon fine
	 */
	public async requestPasswordReset(email: string) {
		const request: INaarGoRequest & { emailAddress: string } = {
			emailAddress: email,
		};
		const response = await this.backend.post<IOkNaarGoResponse>(`auth/request_password_reset`, request);
		return response.result == ENaarGoResponseResult.Ok;
	}

	/**
	 * Imposta una nuova password consumando il codice OTP ricevuto via email.
	 * @returns true se il reset è andato a buon fine
	 */
	public async resetPassword(email: string, code: string, newPassword: string) {
		const md5 = new Md5();
		md5.appendStr(newPassword);
		const encrPsw = md5.end()?.toString() || '';

		const request: INaarGoRequest & { emailAddress: string; code: string; password: string } = {
			emailAddress: email,
			code: code,
			password: encrPsw,
		};
		const response = await this.backend.post<IOkNaarGoResponse>(`auth/reset_password`, request);
		return response.result == ENaarGoResponseResult.Ok;
	}

	public async editUser(name: string, lastname: string, emailAddress: string, language: ELingua) {
		const request: INaarGoEditUserRequest = {
			id: this.currentUser$().id,
			name: name,
			lastname: lastname,
			emailAddress: emailAddress,
			language: language,
		}
		const response = await this.backend.post<INaarGoEditUserResponse>('user/edit', request);
		this.currentUser$.set(response.user);
		await this.setAppLanguage(response.user.language);

		const toast = await this.toast.create({
			message: this.translate.instant('TOAST.EDIT_USER.SUCCESS'),
			duration: this.TOAST_DURATION,
			color: 'success',
			position: 'top',
		});
		await toast.present();
	}

	public async changePassword(oldPassword: string, newPassword: string) {

		const md51 = new Md5();
		md51.appendStr(oldPassword);
		const encrOldPsw = md51.end()?.toString() || '';

		const md52 = new Md5();
		md52.appendStr(newPassword);
		const encrPsw = md52.end()?.toString() || '';

		const request: INaarGoChangePasswordRequest = {
			id: this.currentUser$().id,
			oldPassword: encrOldPsw,
			password: encrPsw,
		}
		await this.backend.post('user/changepassword', request);

		const toast = await this.toast.create({
			message: this.translate.instant('TOAST.CHANGE_PASSWORD.SUCCESS'),
			duration: this.TOAST_DURATION,
			color: 'success',
			position: 'top',
		});
		await toast.present();
	}

	public async deleteUser() {
		const alert = await this.alert.create({
			header: this.translate.instant('ALERT.HEADER.DELETE_USER'),
			message: this.translate.instant('ALERT.MESSAGE.DELETE_USER'),
			buttons: [
				{
					text: this.translate.instant('ALERT.BUTTONS.CANCEL'),
					htmlAttributes: { color: 'danger' },
					role: 'cancel',
				},
				{
					text: this.translate.instant('ALERT.BUTTONS.DELETE'),
					htmlAttributes: { color: 'danger' },
					role: 'confirm',
				}
			]
		});
		await alert.present();

		const { role } = await alert.onWillDismiss();
		if (role == 'cancel')
			return;

		const request: INaarGoDeleteUserRequest = {
			id: this.currentUser$().id,
		}
		await this.backend.post('user/delete', request);
		await this.logout();
	}

	public async refreshUserToken(identity: string = null) {
		const azureCSIdentity = identity ?? this.currentUser$().azureCSIdentity;
		const response = await this.backend.get<INaarGoTokenResponse>(`auth/refreshtoken/${azureCSIdentity}`);
		if (response) {
			const currentUser = this.currentUser$();
			currentUser.azureCSToken = response.csToken;
			currentUser.authToken = response.authToken;

			this.storeUser(currentUser);
		}
	}

	public async logout() {
		this.currentUser$.set(null);
		this.backend.setAuthToken(null);

		await Preferences.remove({ key: USER_TOKEN_KEY });
		await Preferences.remove({ key: USER_IDENTITY_KEY });
		this.url.get('login').navigate();
	}

	private async storeUser(user: NaarGoUserRow) {
		this.currentUser$.set(user || null);
		this.backend.setAuthToken(user?.authToken || null);
		await this.setAppLanguage(user.language);

		await Preferences.set({ key: USER_TOKEN_KEY, value: user?.authToken || null });
		await Preferences.set({ key: USER_IDENTITY_KEY, value: user?.azureCSIdentity || null });
	}
}
